import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import useDownloadPdf from "../../../hooks/useDownloadPdf";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Checkbox } from "../../ui/checkbox";
import { toast } from "sonner";
import { Save, Plus, Trash2, MessageSquare, Truck, History, ScrollText, Calculator, Box, Download, Mail, Share2, Copy, ExternalLink, RefreshCw, Ban, FileText } from "lucide-react";
import { QUOTE_STATUSES, QUOTE_STATUS_LABELS, StatusBadge } from "./OffertesSection";
import WhatsAppTemplateButton from "../WhatsAppTemplateButton";
import QuoteBasisEditor from "./QuoteBasisEditor";
import NAWAddressBlock from "./NAWAddressBlock";
import QuoteConversionPanel from "./QuoteConversionPanel";
import ContactPersonSelector from "../../shared/ContactPersonSelector";
import { applyContactToParty, buildContactOptions } from "../../../services/customerAutofillService";
import { addressMissingFields, isAddressComplete } from "../../../lib/addressHelpers";

const NOTE_TYPES = [
  { value: "algemeen", label: "Algemeen" }, { value: "klantcontact", label: "Klantcontact" },
  { value: "prijsafspraak", label: "Prijsafspraak" }, { value: "documenten", label: "Documenten" },
  { value: "betaling", label: "Betaling" }, { value: "planning", label: "Planning" },
  { value: "waarschuwing", label: "Waarschuwing" },
];
const SHIPMENT_TYPES = [
  { value: "pakket", label: "Pakket" }, { value: "doos", label: "Doos" }, { value: "pallet", label: "Pallet" },
  { value: "meubels", label: "Meubels" }, { value: "verhuizing", label: "Verhuizing" },
  { value: "shop_drop_ship", label: "Shop, Drop & Ship" }, { value: "zakelijk", label: "Zakelijke zending" },
  { value: "voertuig", label: "Auto / voertuig" }, { value: "overig", label: "Overig" },
];
const SHIPMENT_METHODS = [
  { value: "luchtvracht", label: "Luchtvracht" }, { value: "zeevracht", label: "Zeevracht" },
  { value: "pickup_delivery", label: "Pickup & Delivery" }, { value: "express", label: "Express op aanvraag" },
  { value: "te_bepalen", label: "Nog te bepalen" },
];

function fmtTs(ts) { return ts?.slice(0, 16).replace("T", " "); }

export default function OfferteDetail({ quoteId, onChanged }) {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [shipment, setShipment] = useState({});
  const [pricing, setPricing] = useState({});
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState({ note_type: "algemeen", text: "" });
  const [tab, setTab] = useState("overzicht");

  const canEdit = hasPermission("quotes:edit");
  const canDeleteNote = isAdminOrOwner();

  // Phase 18.1 actions: PDF export + email-to-customer + convert-to-pickup
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", cc: "", subject: "", message: "", include_pdf: true, language: "nl", template_type: "", include_share_link: false });
  const [emailSending, setEmailSending] = useState(false);
  const [emailDraftSaving, setEmailDraftSaving] = useState(false);
  const [emailDraftId, setEmailDraftId] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  // Phase 18.7 — shared PDF download UX (loading state, slow-hint, toasts, de-dupe).
  const pdfDl = useDownloadPdf();
  const [convertBusy, setConvertBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Phase 18.11 — share-link state
  const [shareLink, setShareLink] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  // Phase 20C — sales-invoice state (offerte → factuur conversie)
  const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoice, setInvoice] = useState(null);          // {id, invoice_number, status, ...}
  const [invoiceEmailOpen, setInvoiceEmailOpen] = useState(false);
  const [invoiceEmailForm, setInvoiceEmailForm] = useState({ to: "", subject: "", message: "" });
  const [invoiceEmailSending, setInvoiceEmailSending] = useState(false);
  // Batch 3 · Fase 5 (scope-close) — Contactpersoon selectie voor offerte-email.
  // Bepaalt naar welke contactpersoon van de gekoppelde klant de offerte gaat.
  // Reset naar "primary" bij elke opening van de dialog (stale-reset).
  const [emailContactKey, setEmailContactKey] = useState("primary");
  const [selectedContact, setSelectedContact] = useState(null);

  const refreshShareLink = useCallback(async () => {
    if (!quoteId) return;
    try {
      const r = await adminApi.get(`/admin/quotes-v2/${quoteId}/share-link`);
      setShareLink(r.data?.link || null);
    } catch { setShareLink(null); }
  }, [quoteId]);

  const publicShareUrl = useCallback(() => {
    if (!shareLink?.token) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/offerte/${shareLink.token}`;
  }, [shareLink]);

  const onCreateShareLink = async () => {
    setShareBusy(true);
    try {
      const r = await adminApi.post(`/admin/quotes-v2/${quoteId}/share-link`);
      setShareLink(r.data?.link || null);
      toast.success(r.data?.created ? "Share-link aangemaakt." : "Bestaande share-link gevonden.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setShareBusy(false); }
  };

  const onRegenerateShareLink = async () => {
    if (!window.confirm("Weet u zeker dat u een nieuwe share-link wilt genereren? De oude link wordt direct ongeldig.")) return;
    setShareBusy(true);
    try {
      const r = await adminApi.post(`/admin/quotes-v2/${quoteId}/share-link/regenerate`);
      setShareLink(r.data?.link || null);
      toast.success("Nieuwe share-link gegenereerd. De oude is ingetrokken.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setShareBusy(false); }
  };

  const onRevokeShareLink = async () => {
    if (!window.confirm("Share-link intrekken? De klant kan de offerte daarna niet meer via deze link openen.")) return;
    setShareBusy(true);
    try {
      await adminApi.post(`/admin/quotes-v2/${quoteId}/share-link/revoke`);
      setShareLink(null);
      toast.success("Share-link ingetrokken.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setShareBusy(false); }
  };

  const onCopyShareLink = async () => {
    const url = publicShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share-link gekopieerd naar klembord.");
    } catch {
      toast.info(`Kopieer handmatig: ${url}`);
    }
  };

  const onOpenShareLink = () => {
    const url = publicShareUrl();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => { refreshShareLink(); }, [refreshShareLink]);

  // Load templates once when an OfferteDetail mounts so the picker is ready
  useEffect(() => {
    let active = true;
    adminApi.get("/admin/quote-templates").then((r) => {
      if (active) setTemplates(r.data?.templates || []);
    }).catch(() => { /* templates optional — fall back to empty dropdown */ });
    return () => { active = false; };
  }, []);

  const applyTemplate = (key, language) => {
    if (!key) return;
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    const lang = language === "en" ? "en" : "nl";
    const subject = lang === "en" ? tpl.subject_en : tpl.subject_nl;
    const body = lang === "en" ? tpl.body_en : tpl.body_nl;
    setEmailForm((f) => ({ ...f, subject, message: body, template_type: key, language: lang }));
  };

  const onDownloadPdf = () =>
    pdfDl.download(
      async () => {
        if (!quoteId) throw new Error("missing quoteId");
        const r = await adminApi.get(`/admin/quotes-v2/${quoteId}/pdf`, { responseType: "blob" });
        return {
          blob: new Blob([r.data], { type: "application/pdf" }),
          filename: `offerte-${(quoteId || "").slice(0, 8)}.pdf`,
        };
      },
      {
        loadingMessage: "Offerte wordt gegenereerd…",
        successMessage: "Offerte klaar — download gestart.",
        errorMessage: "PDF kon niet worden gedownload. Probeer het opnieuw.",
        taskId: `quote-pdf-${quoteId}`,
      },
    );

  const openEmailDialog = async () => {
    const q = data?.quote || {};
    const fullName = [q.first_name, q.last_name].filter(Boolean).join(" ") || q.name || "klant";

    // Try to load an existing draft for this quote — overrides defaults if present.
    let draft = null;
    try {
      const r = await adminApi.get(`/admin/quotes-v2/${quoteId}/email/draft`);
      draft = r.data?.draft || null;
    } catch { /* no draft yet — fall back to defaults */ }

    if (draft) {
      setEmailDraftId(draft.id);
      setEmailForm({
        to: draft.to || q.email || "",
        cc: (draft.cc || []).join(", "),
        subject: draft.subject || "Uw offerte van 5999Cargo",
        message: draft.body || "",
        include_pdf: draft.include_pdf !== false,
        language: draft.language === "en" ? "en" : "nl",
        template_type: draft.template_type || "",
        include_share_link: !!draft.include_share_link,
      });
      toast.info("Opgeslagen concept geladen.");
    } else {
      setEmailDraftId(null);
      setEmailForm({
        to: q.email || "",
        cc: "",
        subject: "Uw offerte van 5999Cargo",
        message:
          `Geachte ${fullName},\n\n` +
          `Bijgaand ontvangt u de offerte voor uw aanvraag bij 5999Cargo.\n\n` +
          `Controleer de gegevens en neem contact met ons op als er iets aangepast moet worden.\n\n` +
          `Met vriendelijke groet,\n5999Cargo Group Europe`,
        include_pdf: true,
        language: "nl",
        template_type: "",
        include_share_link: false,
      });
    }
    // Batch 3 · Fase 5 — stale-reset: nieuwe dialog-open = terug naar
    // primair contact. Vermijdt dat de contactkeuze van een vorige
    // verzending "blijft plakken".
    setEmailContactKey("primary");
    setSelectedContact(null);
    setEmailDialogOpen(true);
  };

  const onSaveDraft = async () => {
    setEmailDraftSaving(true);
    try {
      const payload = {
        to: emailForm.to,
        cc: emailForm.cc
          ? emailForm.cc.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
        subject: emailForm.subject,
        body: emailForm.message,
        language: emailForm.language,
        template_type: emailForm.template_type || null,
        include_pdf: emailForm.include_pdf,
      };
      const r = await adminApi.put(`/admin/quotes-v2/${quoteId}/email/draft`, payload);
      setEmailDraftId(r.data?.draft?.id || null);
      toast.success("Concept opgeslagen.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailDraftSaving(false); }
  };

  const onSendEmail = async () => {
    if (!emailForm.to || !emailForm.to.includes("@")) {
      toast.error("Vul een geldig e-mailadres in.");
      return;
    }
    setEmailSending(true);
    try {
      const payload = {
        to: emailForm.to,
        subject: emailForm.subject,
        message: emailForm.message,
        include_pdf: emailForm.include_pdf,
        include_share_link: !!emailForm.include_share_link,
        cc: emailForm.cc
          ? emailForm.cc.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
      };
      // Batch 3 · Fase 5 — Contact snapshot meesturen zodat de backend
      // valideert (cross-customer) en persist voor toekomstige reminders.
      if (selectedContact) {
        payload.selected_contact_id = emailContactKey === "primary" ? "primary" : emailContactKey.replace(/^contact-/, "");
        payload.selected_contact_email = selectedContact.email || null;
        payload.selected_contact_name = selectedContact.name || null;
        payload.selected_contact_phone = selectedContact.phone || null;
      }
      const r = await adminApi.post(`/admin/quotes-v2/${quoteId}/email`, payload);
      const status = r.data?.status;
      if (status === "sent") {
        toast.success("E-mail verzonden naar klant");
      } else if (status === "skipped_no_key") {
        toast.warning("RESEND_API_KEY ontbreekt — e-mail is gelogd maar niet verzonden");
      } else if (status === "failed") {
        toast.error(`E-mail mislukt: ${r.data?.error || "onbekende fout"}`);
      } else {
        toast.info(`E-mailstatus: ${status}`);
      }
      setEmailDialogOpen(false);
      load();
      // refresh share-link state — the email may have auto-created one
      refreshShareLink();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailSending(false); }
  };

  const onConvertToPickup = async () => {
    const linked = data?.quote?.pickup_id;
    const msg = linked
      ? "Er is al een Pickup & Delivery-afspraak gekoppeld aan deze offerte. Klik OK om de bestaande afspraak te openen."
      : "Deze offerte omzetten naar Pickup & Delivery afspraak?";
    if (!window.confirm(msg)) return;
    setConvertBusy(true);
    try {
      const r = await adminApi.post(`/admin/quotes-v2/${quoteId}/convert-to-pickup`);
      const pid = r.data?.id;
      const already = r.data?.already_converted;
      toast.success(
        already
          ? `Bestaande pickup-afspraak gekoppeld (${(pid || "").slice(0, 8)}…)`
          : `Pickup-afspraak aangemaakt (${(pid || "").slice(0, 8)}…)`,
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConvertBusy(false); }
  };

  // Phase 20C — Offerte → Factuur conversie. Server is idempotent op quote_id,
  // dus we lekken nooit dubbele facturen. Status-check (goedgekeurd/akkoord)
  // wordt server-side gehandhaafd; UI vraagt vooraf bevestiging.
  const onCreateInvoice = async () => {
    setInvoiceConfirmOpen(false);
    setInvoiceBusy(true);
    try {
      const isApproved = ["goedgekeurd", "akkoord", "accepted", "approved"].includes(
        (data?.quote?.status || "").toLowerCase(),
      );
      const r = await adminApi.post(`/admin/quotes-v2/${quoteId}/convert-to-invoice`, {
        confirm_no_accept_status: !isApproved,
        due_days: 14,
      });
      const inv = r.data?.invoice;
      setInvoice(inv || null);
      toast.success(
        r.data?.already_exists
          ? `Bestaande factuur ${inv?.invoice_number} gekoppeld.`
          : `Factuur ${inv?.invoice_number} aangemaakt.`,
      );
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setInvoiceBusy(false); }
  };

  const onDownloadInvoice = async () => {
    if (!invoice?.id) return;
    try {
      const r = await adminApi.get(`/admin/sales-invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Factuur-PDF geopend in nieuw tabblad.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openInvoiceEmailDialog = () => {
    if (!invoice) return;
    setInvoiceEmailForm({
      to: invoice.customer_email || data?.quote?.email || "",
      subject: `Factuur van 5999Cargo - ${invoice.invoice_number}`,
      message: "",
    });
    setInvoiceEmailOpen(true);
  };

  const onSendInvoiceEmail = async () => {
    if (!invoice?.id) return;
    setInvoiceEmailSending(true);
    try {
      const r = await adminApi.post(`/admin/sales-invoices/${invoice.id}/email`, {
        to: invoiceEmailForm.to || null,
        subject: invoiceEmailForm.subject || null,
        message: invoiceEmailForm.message || null,
        include_pdf: true,
      });
      const status = r.data?.email_status;
      if (status === "skipped_no_key") {
        toast.info("E-mailadapter geen API key — bericht is genoteerd maar niet verzonden in preview.");
      } else if (status === "sent") {
        toast.success(`Factuur verzonden naar ${r.data?.to}.`);
      } else {
        toast.warning(`Factuur-status: ${status}`);
      }
      setInvoiceEmailOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setInvoiceEmailSending(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        adminApi.get(`/admin/quotes-v2/${quoteId}`),
        adminApi.get(`/admin/quotes-v2/${quoteId}/timeline`).catch(() => ({ data: [] })),
        adminApi.get(`/admin/quotes-v2/${quoteId}/audit-logs`).catch(() => ({ data: [] })),
      ]);
      setData(r1.data);
      const q = r1.data.quote;
      setForm({
        first_name: q.first_name || "", last_name: q.last_name || "", company_name: q.company_name || "",
        email: q.email || "", phone: q.phone || "", whatsapp: q.whatsapp || "",
        pickup_address: q.pickup_address || "", delivery_address: q.delivery_address || "",
        destination_country: q.destination_country || q.destination || "",
        destination_contact: q.destination_contact || "", destination_phone: q.destination_phone || "",
        // Phase 20E — gestructureerde NAW
        street: q.street || "", house_number: q.house_number || "",
        house_number_addition: q.house_number_addition || "",
        postal_code: q.postal_code || "", city: q.city || "", country: q.country || "",
        description: q.description || "",
      });
      setShipment(q.shipment || {});
      setPricing(q.pricing || {});
      setTimeline(r2.data || []);
      setAuditLogs(r3.data || []);
      // Phase 20C — load linked sales-invoice if present
      if (q.sales_invoice_id) {
        try {
          const inv = await adminApi.get(`/admin/sales-invoices/${q.sales_invoice_id}`);
          setInvoice(inv.data || null);
        } catch { setInvoice(null); }
      } else {
        setInvoice(null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...Object.fromEntries(Object.entries(form).filter(([_k, v]) => v !== "")),
        shipment, pricing,
      };
      await adminApi.put(`/admin/quotes-v2/${quoteId}`, payload);
      toast.success("Offerte bijgewerkt");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onChangeStatus = async (status) => {
    try {
      await adminApi.put(`/admin/quotes-v2/${quoteId}/status`, { status });
      load(); onChanged?.();
      toast.success("Status bijgewerkt");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onAddNote = async () => {
    if (!newNote.text.trim()) { toast.error("Notitietekst is verplicht."); return; }
    try {
      await adminApi.post(`/admin/quotes-v2/${quoteId}/notes`, newNote);
      setNewNote({ note_type: "algemeen", text: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDeleteNote = async (noteId) => {
    if (!window.confirm("Notitie verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/quotes-v2/${quoteId}/notes/${noteId}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <div className="text-gray-500">Offerte laden...</div>;
  const q = data.quote;
  const notes = data.notes || [];
  const customer = data.customer;

  const numField = (label, key, target, setter, suffix) => (
    <div>
      <Label className="text-xs uppercase tracking-wide font-bold">{label}{suffix && <span className="text-gray-400 normal-case font-normal"> ({suffix})</span>}</Label>
      <Input
        type="number" step="0.01"
        value={target[key] ?? ""}
        onChange={(e) => setter({ ...target, [key]: e.target.value === "" ? null : parseFloat(e.target.value) })}
        disabled={!canEdit} className="rounded-none mt-1"
        data-testid={`offerte-${key}`}
      />
    </div>
  );

  return (
    <div data-testid={`offerte-detail-${quoteId}`} className="space-y-4">
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-bold">Offerte</div>
            <h1 className="font-chivo font-black text-2xl text-[#0A192F] mt-1">{q.name || "—"}</h1>
            <div className="text-xs text-gray-500 mt-2">
              ID: <span className="font-mono">{q.id}</span> · Aangemaakt {fmtTs(q.created_at)}
              {q.source && <> · Bron: {q.source}</>}
            </div>
            {customer && <div className="text-xs text-[#0055FF] mt-1">Gekoppeld aan klant: {customer.email || customer.id}</div>}
            {q.pending_customer_review && (
              <div className="mt-2 inline-block bg-yellow-100 border border-yellow-300 px-2 py-1 text-xs text-yellow-800 font-bold">
                ⚠ Mogelijke dubbele klant — handmatig koppelen vereist
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <WhatsAppTemplateButton quoteId={q.id} testIdPrefix="offerte-wa" />
            <Button
              onClick={onDownloadPdf}
              variant="outline" size="sm"
              className="rounded-none border-gray-300 text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
              data-testid="offerte-pdf-export"
              disabled={pdfBusy || pdfDl.loading}
            >
              <Download className="w-4 h-4 mr-1" />{" "}
              {pdfDl.loading ? "PDF wordt gegenereerd..." : "PDF"}
            </Button>
            <Button
              onClick={openEmailDialog}
              variant="outline" size="sm"
              className="rounded-none border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
              data-testid="offerte-email-customer"
              disabled={!canEdit || !q.email}
              title={!q.email ? "Geen e-mailadres bij deze offerte" : undefined}
            >
              <Mail className="w-4 h-4 mr-1" /> Mail naar klant
            </Button>
            <Button
              onClick={onConvertToPickup}
              variant="outline" size="sm"
              className="rounded-none border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
              data-testid="offerte-convert-pickup"
              disabled={!canEdit || convertBusy}
            >
              <Truck className="w-4 h-4 mr-1" /> {q.pickup_id ? "Bekijk pickup-afspraak" : "Omzetten naar Pickup & Delivery"}
            </Button>
            {/* Phase 20C — Offerte → Factuur. Toont per state: aanmaken OF beheer. */}
            {invoice ? (
              <>
                <Button
                  onClick={onDownloadInvoice}
                  variant="outline" size="sm"
                  className="rounded-none border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                  data-testid="offerte-invoice-download"
                >
                  <FileText className="w-4 h-4 mr-1" /> Factuur {invoice.invoice_number}
                </Button>
                <Button
                  onClick={openInvoiceEmailDialog}
                  variant="outline" size="sm"
                  className="rounded-none border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                  data-testid="offerte-invoice-email"
                  disabled={!canEdit || !invoice.customer_email}
                  title={!invoice.customer_email ? "Geen e-mailadres bij deze factuur" : undefined}
                >
                  <Mail className="w-4 h-4 mr-1" /> Verstuur factuur
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setInvoiceConfirmOpen(true)}
                variant="outline" size="sm"
                className="rounded-none border-[#082c50] bg-[#082c50] text-white hover:bg-[#1a5193]"
                data-testid="offerte-create-invoice"
                disabled={!canEdit || invoiceBusy}
              >
                <FileText className="w-4 h-4 mr-1" /> {invoiceBusy ? "Aanmaken…" : "Maak factuur"}
              </Button>
            )}
            <Label className="text-xs uppercase tracking-wide font-bold ml-2">Status:</Label>
            {canEdit ? (
              <Select value={QUOTE_STATUSES.includes(q.status) ? q.status : "nieuw"} onValueChange={onChangeStatus}>
                <SelectTrigger className="rounded-none w-56" data-testid="offerte-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{QUOTE_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            ) : <StatusBadge status={q.status} />}
          </div>
        </div>
      </div>

      {/* Phase 18.11 — Deelbare offerte */}
      <div className="bg-white border border-gray-200 p-4 sm:p-5" data-testid="offerte-share-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[#0A192F]">
            <Share2 className="w-4 h-4 text-[#1a5193]" />
            <h3 className="font-chivo font-bold text-sm uppercase tracking-wide">Deelbare offerte</h3>
            {shareLink ? (
              <span data-testid="offerte-share-status" className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                Actief · …{shareLink.token_last4}
              </span>
            ) : (
              <span data-testid="offerte-share-status" className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200">
                Geen actieve link
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!shareLink ? (
              <Button
                size="sm" variant="default"
                onClick={onCreateShareLink} disabled={!canEdit || shareBusy}
                data-testid="offerte-share-create"
                className="rounded-none bg-[#1a5193] hover:bg-[#194d8d] text-white"
              >
                <Share2 className="w-4 h-4 mr-1" /> Share-link genereren
              </Button>
            ) : (
              <>
                <Button
                  size="sm" variant="outline"
                  onClick={onCopyShareLink} disabled={shareBusy}
                  data-testid="offerte-share-copy"
                  className="rounded-none border-[#1a5193] text-[#1a5193] hover:bg-[#1a5193] hover:text-white"
                >
                  <Copy className="w-4 h-4 mr-1" /> Kopieer share-link
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={onOpenShareLink} disabled={shareBusy}
                  data-testid="offerte-share-open"
                  className="rounded-none border-gray-300 text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-1" /> Open klantpagina
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={onRegenerateShareLink} disabled={!canEdit || shareBusy}
                  data-testid="offerte-share-regenerate"
                  className="rounded-none border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Nieuwe link
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={onRevokeShareLink} disabled={!canEdit || shareBusy}
                  data-testid="offerte-share-revoke"
                  className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
                >
                  <Ban className="w-4 h-4 mr-1" /> Link intrekken
                </Button>
              </>
            )}
          </div>
        </div>
        {shareLink && (
          <div className="mt-3 text-xs text-gray-600 space-y-1">
            <div className="font-mono break-all bg-zinc-50 border border-zinc-200 rounded px-2.5 py-1.5" data-testid="offerte-share-url">
              {publicShareUrl()}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
              {shareLink.view_count != null && <span>Bekeken: <strong className="text-[#0A192F]">{shareLink.view_count}×</strong></span>}
              {shareLink.last_viewed_at && <span>Laatst bekeken: {new Date(shareLink.last_viewed_at).toLocaleString("nl-NL")}</span>}
              {shareLink.pdf_download_count != null && <span>PDF gedownload: <strong className="text-[#0A192F]">{shareLink.pdf_download_count}×</strong></span>}
            </div>
          </div>
        )}
        {!shareLink && (
          <p className="mt-3 text-xs text-gray-500">
            Maak een veilige share-link aan zodat de klant deze offerte online kan bekijken en als PDF kan downloaden, zonder backoffice-login.
          </p>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-white border border-gray-200 p-0 h-auto flex flex-wrap" data-testid="offerte-tabs">
          <TabsTrigger value="overzicht" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-overzicht">Overzicht</TabsTrigger>
          <TabsTrigger value="klant" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-klant">Klantgegevens</TabsTrigger>
          <TabsTrigger value="zending" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-zending">Zending</TabsTrigger>
          <TabsTrigger value="prijs" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-prijs">Prijsberekening</TabsTrigger>
          <TabsTrigger value="conversie" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-conversie">Klantmatching &amp; Conversie</TabsTrigger>
          <TabsTrigger value="notities" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-notities">Notities ({notes.length})</TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="offerte-tab-audit">Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4">Overzicht</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Klant</div>
              <div className="font-medium mt-1">{q.name || "—"}</div>
              <div className="text-xs text-gray-500">{q.email}</div>
              <div className="text-xs text-gray-500">{q.phone}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Service</div>
              <div className="font-medium mt-1">{q.service || shipment.method || "—"}</div>
              <div className="text-xs text-gray-500">Type: {shipment.shipment_type || "—"}</div>
              <div className="text-xs text-gray-500">Bestemming: {q.destination_country || q.destination}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Totaal incl. BTW</div>
              <div className="font-chivo font-black text-2xl text-[#0A192F] mt-1">
                {pricing.total_incl_vat != null ? `€ ${Number(pricing.total_incl_vat).toFixed(2)}` : "—"}
              </div>
              <div className="text-xs text-gray-500">excl: {pricing.subtotal_excl_vat != null ? `€ ${Number(pricing.subtotal_excl_vat).toFixed(2)}` : "—"}</div>
            </div>
          </div>
          {q.description && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Omschrijving</div>
              <p className="text-sm whitespace-pre-wrap">{q.description}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="klant" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4">Klantgegevens aanpassen</h2>
          <p className="text-xs text-gray-500 mb-3">Wijzigingen hier passen alleen de offerte aan. Voor klantkaart-update: ga naar Klanten → klantdetail.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ["first_name", "Voornaam"], ["last_name", "Achternaam"], ["company_name", "Bedrijfsnaam"],
              ["email", "E-mailadres"], ["phone", "Telefoonnummer"], ["whatsapp", "WhatsApp nummer"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide font-bold">{l}</Label>
                <Input
                  data-testid={`offerte-cust-${k}`}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  disabled={!canEdit} className="rounded-none mt-1"
                />
              </div>
            ))}
          </div>

          {/* Phase 20F — Gestructureerde NAW met PDOK auto-lookup */}
          <div className="mt-5">
            <NAWAddressBlock
              value={{
                street: form.street, house_number: form.house_number,
                house_number_addition: form.house_number_addition,
                postal_code: form.postal_code, city: form.city, country: form.country,
              }}
              onChange={(patch) => setForm({ ...form, ...patch })}
              disabled={!canEdit}
              testidPrefix="offerte-cust"
              title="Adresgegevens (NAW)"
              hint="Vul postcode + huisnummer in voor automatisch ophalen van straat en plaats (NL). Bestaande offertes zonder NAW blijven werken via legacy pickup-adres."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {[
              ["pickup_address", "Pickup adres (legacy)"], ["delivery_address", "Afleveradres"],
              ["destination_country", "Land van bestemming"], ["destination_contact", "Contactpersoon op bestemming"],
              ["destination_phone", "Telefoonnummer bestemming"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide font-bold">{l}</Label>
                <Input
                  data-testid={`offerte-cust-${k}`}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  disabled={!canEdit} className="rounded-none mt-1"
                />
              </div>
            ))}
          </div>
          {canEdit && <Button onClick={onSave} disabled={saving} data-testid="offerte-save-button" className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />{saving ? "Opslaan..." : "Wijzigingen opslaan"}</Button>}
        </TabsContent>

        <TabsContent value="zending" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4 flex items-center gap-2"><Box className="w-5 h-5 text-[#0055FF]" />Zendinggegevens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Type zending</Label>
              <Select value={shipment.shipment_type || ""} onValueChange={(v) => setShipment({ ...shipment, shipment_type: v })} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1" data-testid="offerte-shipment-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{SHIPMENT_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Verzendmethode</Label>
              <Select value={shipment.method || ""} onValueChange={(v) => setShipment({ ...shipment, method: v })} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1" data-testid="offerte-shipment-method"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{SHIPMENT_METHODS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {numField("Aantal dozen", "boxes", shipment, setShipment)}
            {numField("Gewicht", "weight_kg", shipment, setShipment, "kg")}
            {numField("Lengte", "length_cm", shipment, setShipment, "cm")}
            {numField("Breedte", "width_cm", shipment, setShipment, "cm")}
            {numField("Hoogte", "height_cm", shipment, setShipment, "cm")}
            {numField("CBM", "cbm", shipment, setShipment, "m³")}
            {numField("Vol. weight (lucht)", "volumetric_weight_air", shipment, setShipment, "kg")}
            {numField("Waarde goederen", "value_eur", shipment, setShipment, "EUR")}
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Factuur / aankoopbewijs</Label>
              <Select value={shipment.has_invoice == null ? "" : String(shipment.has_invoice)} onValueChange={(v) => setShipment({ ...shipment, has_invoice: v === "true" })} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1" data-testid="offerte-has-invoice"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="true">Ja</SelectItem><SelectItem value="false">Nee</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Paklijst</Label>
              <Select value={shipment.has_packing_list == null ? "" : String(shipment.has_packing_list)} onValueChange={(v) => setShipment({ ...shipment, has_packing_list: v === "true" })} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1" data-testid="offerte-has-packing"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="true">Ja</SelectItem><SelectItem value="false">Nee</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs uppercase tracking-wide font-bold">Omschrijving goederen</Label>
            <Textarea data-testid="offerte-description" rows={3} value={shipment.description || form.description || ""} onChange={(e) => setShipment({ ...shipment, description: e.target.value })} disabled={!canEdit} className="rounded-none mt-1" />
          </div>
          {canEdit && <Button onClick={onSave} disabled={saving} className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />Opslaan</Button>}
        </TabsContent>

        <TabsContent value="prijs" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4 flex items-center gap-2"><Calculator className="w-5 h-5 text-[#0055FF]" />Prijsberekening</h2>

          {/*
            Phase 20.1 — QuoteBasisEditor renders the mode-aware editor with
            live auto-recompute. For legacy quotes (no quote_basis) it shows a
            migration banner and falls through to the original numField grid
            below for backwards-compatible manual editing.
          */}
          <QuoteBasisEditor
            quote={q}
            canEdit={canEdit}
            onSaved={() => { load(); onChanged?.(); }}
          />

          {!q.quote_basis ? (
            <>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="font-chivo font-bold text-sm text-[#0A192F] mb-2 uppercase tracking-wide">
                  Legacy handmatige prijsberekening
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Deze offerte gebruikt nog de oude single-shipment layout. U kunt hieronder handmatig waarden invoeren, of bovenaan migreren naar één van de drie modi voor automatische berekening.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {numField("Prijs per kg", "price_per_kg", pricing, setPricing, "EUR")}
                  {numField("Prijs per m³ / CBM", "price_per_cbm", pricing, setPricing, "EUR")}
                  {numField("Administratiekosten", "admin_fee", pricing, setPricing, "EUR")}
                  {numField("Documentatiekosten", "documentation_fee", pricing, setPricing, "EUR")}
                  {numField("Pickupkosten", "pickup_fee", pricing, setPricing, "EUR")}
                  {numField("Voorrijkosten", "callout_fee", pricing, setPricing, "EUR")}
                  {numField("KM-vergoeding", "km_fee", pricing, setPricing, "EUR")}
                  {numField("Extra handlingkosten", "extra_handling", pricing, setPricing, "EUR")}
                  {numField("Korting", "discount", pricing, setPricing, "EUR")}
                  {numField("BTW-percentage", "vat_percent", pricing, setPricing, "%")}
                  {numField("Totaal excl. BTW", "subtotal_excl_vat", pricing, setPricing, "EUR")}
                  {numField("BTW bedrag", "vat_amount", pricing, setPricing, "EUR")}
                  {numField("Totaal incl. BTW", "total_incl_vat", pricing, setPricing, "EUR")}
                </div>
                {canEdit && <Button onClick={onSave} disabled={saving} className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="offerte-legacy-save"><Save className="w-4 h-4 mr-1" />Opslaan (legacy)</Button>}
              </div>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="conversie" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4" data-testid="conversie-header">
            Klantmatching &amp; Conversie naar zending
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Deze module leest uitsluitend uit de backend-endpoints (klantmatching,
            conversie en mapping-report). Alle beslissingen worden server-side genomen —
            de UI toont enkel het resultaat.
          </p>
          <QuoteConversionPanel quote={q} onQuoteChanged={() => { load(); onChanged?.(); }} />
        </TabsContent>

        <TabsContent value="notities" className="bg-white border border-gray-200 p-6 mt-2">
          <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 px-3 py-2 mb-3">
            Notities zijn alleen intern zichtbaar. Klanten zien deze nooit.
          </div>
          {canEdit && (
            <div className="bg-[#F8F9FA] border border-gray-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2">
              <Select value={newNote.note_type} onValueChange={(v) => setNewNote({ ...newNote, note_type: v })}>
                <SelectTrigger className="rounded-none" data-testid="note-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{NOTE_TYPES.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="note-text-input" placeholder="Notitietekst..." value={newNote.text} onChange={(e) => setNewNote({ ...newNote, text: e.target.value })} className="rounded-none" />
              <Button onClick={onAddNote} data-testid="note-add-button" className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Plus className="w-4 h-4 mr-1" />Toevoegen</Button>
            </div>
          )}
          {notes.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen notities.</p>
          ) : (
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.id} className="border border-gray-200 p-3 flex items-start justify-between gap-3" data-testid={`note-item-${n.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">{NOTE_TYPES.find((t) => t.value === n.note_type)?.label || n.note_type}</span>
                      <span className="text-xs text-gray-500">{n.author_email} · {fmtTs(n.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{n.text}</p>
                  </div>
                  {canDeleteNote && (
                    <button onClick={() => onDeleteNote(n.id)} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`note-delete-${n.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="bg-white border border-gray-200 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-4 flex items-center gap-2"><History className="w-5 h-5 text-[#0055FF]" />Timeline ({timeline.length})</h3>
          {timeline.length === 0 ? <p className="text-sm text-gray-500">Nog geen events.</p> : (
            <ul className="relative pl-6 border-l-2 border-gray-200 space-y-4" data-testid="offerte-timeline-list">
              {timeline.map((e, i) => (
                <li key={i} className="relative" data-testid={`offerte-timeline-event-${i}`}>
                  <div className="absolute -left-[1.85rem] top-1 w-3 h-3 rounded-full bg-[#0055FF] border-2 border-white"></div>
                  <div className="text-xs text-gray-500">{fmtTs(e.ts)}</div>
                  <div className="font-chivo font-bold text-sm text-[#0A192F] mt-0.5">{e.description}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    <span className="inline-block bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-bold mr-1">{e.type}</span>
                    door {e.actor}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="audit" className="bg-white border border-gray-200 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-3 flex items-center gap-2"><ScrollText className="w-5 h-5 text-[#0055FF]" />Audit log ({auditLogs.length})</h3>
          {auditLogs.length === 0 ? <p className="text-sm text-gray-500">Nog geen log-entries.</p> : (
            <ul className="space-y-2 text-xs" data-testid="offerte-audit-list">
              {auditLogs.map((a) => (
                <li key={a.id} className="border border-gray-200 p-2" data-testid={`offerte-audit-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">{fmtTs(a.created_at)}</span>
                    <span className="font-chivo font-bold">{a.action}</span>
                    <span className="text-gray-500">door {a.actor_email || "—"}</span>
                  </div>
                  {(a.old_value || a.new_value) && (
                    <div className="mt-1 text-gray-600">
                      {a.old_value && <span>oud: <code className="bg-gray-100 px-1">{JSON.stringify(a.old_value)}</code></span>}
                      {a.new_value && <span className="ml-2">nieuw: <code className="bg-green-50 px-1">{JSON.stringify(a.new_value)}</code></span>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      {q.pickup_id && (
        <div className="bg-blue-50 border border-blue-200 p-4" data-testid="offerte-pickup-link">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#0055FF]" />
              <span className="text-sm">
                Gekoppeld aan Pickup &amp; Delivery afspraak <code className="bg-white px-1.5 py-0.5 border border-blue-200">{q.pickup_id.slice(0, 8)}…</code>
              </span>
            </div>
          </div>
        </div>
      )}

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="offerte-email-dialog">
          <DialogHeader>
            <DialogTitle>Offerte e-mailen naar klant</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {emailDraftId && (
              <div className="bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-900" data-testid="offerte-email-draft-loaded">
                Opgeslagen concept geladen — wijzigingen worden bij "Concept opslaan" overschreven.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold">Taal</Label>
                <Select
                  value={emailForm.language}
                  onValueChange={(v) => {
                    setEmailForm((f) => ({ ...f, language: v }));
                    if (emailForm.template_type) applyTemplate(emailForm.template_type, v);
                  }}
                >
                  <SelectTrigger data-testid="offerte-email-language" className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold">Voorbeeldtekst</Label>
                <Select
                  value={emailForm.template_type || "none"}
                  onValueChange={(v) => {
                    if (v === "none") {
                      setEmailForm((f) => ({ ...f, template_type: "" }));
                    } else {
                      applyTemplate(v, emailForm.language);
                    }
                  }}
                >
                  <SelectTrigger data-testid="offerte-email-template" className="rounded-none mt-1">
                    <SelectValue placeholder="Kies template…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— geen template —</SelectItem>
                    {templates.map((tpl) => (
                      <SelectItem key={tpl.key} value={tpl.key} data-testid={`offerte-email-template-${tpl.key}`}>
                        {emailForm.language === "en" ? tpl.label_en : tpl.label_nl}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Batch 3 · Fase 5 (scope-close) — Contactpersoon-selector voor
                gekoppelde klant. customer_id blijft leidend; alleen naam/
                e-mail/telefoon in de mail worden gepatcht. Wordt automatisch
                verborgen als de klant geen extra contactpersonen heeft. */}
            <ContactPersonSelector
              customer={customer}
              value={emailContactKey}
              onSelect={(key, contact) => {
                setEmailContactKey(key);
                setSelectedContact(contact);
                // Update `to`-veld met contact-email (of laat leeg als geen email)
                if (contact?.email) {
                  setEmailForm((f) => ({ ...f, to: contact.email }));
                } else if (contact) {
                  // Contact zonder e-mail: valideringswaarschuwing bij verzenden.
                  setEmailForm((f) => ({ ...f, to: "" }));
                  toast.info(`${contact.name} heeft geen e-mailadres. Vul het handmatig in of kies een andere contactpersoon.`);
                }
              }}
              testIdPrefix="offerte-email-contact"
              hint="Selecteer een specifieke contactpersoon van de klant (bv. boekhouder)."
            />
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Aan</Label>
              <Input
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                className="rounded-none mt-1" data-testid="offerte-email-to"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">CC (optioneel, comma-separated)</Label>
              <Input
                value={emailForm.cc}
                onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                className="rounded-none mt-1" placeholder="manager@5999cargo.nl, planning@5999cargo.nl"
                data-testid="offerte-email-cc"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Onderwerp</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                className="rounded-none mt-1" data-testid="offerte-email-subject"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Bericht</Label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                className="rounded-none mt-1 min-h-[180px] font-mono text-sm"
                data-testid="offerte-email-message"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="offerte-email-include-pdf"
                checked={emailForm.include_pdf}
                onCheckedChange={(v) => setEmailForm({ ...emailForm, include_pdf: !!v })}
                data-testid="offerte-email-include-pdf"
              />
              <Label htmlFor="offerte-email-include-pdf" className="text-sm">
                PDF-offerte meesturen als bijlage
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="offerte-email-include-share-link"
                checked={!!emailForm.include_share_link}
                onCheckedChange={(v) => setEmailForm({ ...emailForm, include_share_link: !!v })}
                data-testid="offerte-email-include-share-link"
                className="mt-0.5"
              />
              <Label htmlFor="offerte-email-include-share-link" className="text-sm leading-snug">
                Share-link toevoegen aan e-mail
                <span className="block text-xs text-gray-500 font-normal mt-0.5">
                  De klant kan de offerte daarmee online bekijken. Als er nog geen share-link bestaat wordt deze automatisch aangemaakt.
                </span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEmailDialogOpen(false)} data-testid="offerte-email-cancel">
              Annuleren
            </Button>
            <Button
              variant="outline"
              className="rounded-none border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
              onClick={onSaveDraft}
              disabled={emailDraftSaving}
              data-testid="offerte-email-save-draft"
            >
              {emailDraftSaving ? "Opslaan..." : "Concept opslaan"}
            </Button>
            <Button
              className="rounded-none bg-[#0055FF] hover:bg-[#0044CC] text-white"
              onClick={onSendEmail}
              disabled={emailSending}
              data-testid="offerte-email-send"
            >
              {emailSending ? "Verzenden..." : "Verzenden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 20C — Bevestigingsdialog voor offerte → factuur conversie */}
      <Dialog open={invoiceConfirmOpen} onOpenChange={setInvoiceConfirmOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="offerte-invoice-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-xl">Factuur aanmaken</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-gray-700">Factuur aanmaken op basis van deze offerte?</p>
            {data?.quote && (() => {
              const qStatus = (data.quote.status || "").toLowerCase();
              const approved = ["goedgekeurd", "akkoord", "accepted", "approved"].includes(qStatus);
              const total = data.quote.pricing?.total_incl_vat || 0;
              const vatPct = data.quote.pricing?.vat_percent;
              const basisLabel = {
                lines: "Regels / items",
                total_volume: "Totaalvolume",
                fixed: "Vaste prijs",
              }[data.quote.quote_basis] || "Single shipment (legacy)";
              const addressMissing = addressMissingFields(data.quote);
              const addressOk = isAddressComplete(data.quote);
              return (
                <>
                  {!approved && (
                    <div className="bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs" data-testid="invoice-not-approved-warning">
                      <strong>Let op:</strong> Deze offerte is nog niet gemarkeerd als "goedgekeurd". U kunt toch een factuur aanmaken — bevestig hieronder om door te gaan.
                    </div>
                  )}
                  {!addressOk && (
                    <div className="bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs" data-testid="invoice-incomplete-address-warning">
                      <strong>Adres onvolledig:</strong> Een factuur hoort volledige adresgegevens te bevatten. Ontbrekend: <span className="font-mono">{addressMissing.join(", ")}</span>. U kunt eerst aanvullen via de Klantgegevens-tab, of toch doorgaan.
                    </div>
                  )}
                  <div className="bg-gray-50 border border-gray-200 p-3 space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-gray-500">Klant</span><span className="font-mono">{data.quote.name || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Offertebasis</span><span>{basisLabel}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">BTW</span><span className="font-mono">{vatPct != null ? `${vatPct}%` : "—"}</span></div>
                    <div className="flex justify-between border-t pt-1.5 mt-1.5"><span className="font-bold text-gray-700">Totaal incl. BTW</span><span className="font-mono font-bold text-[#082c50]">€ {Number(total).toFixed(2)}</span></div>
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setInvoiceConfirmOpen(false)} data-testid="invoice-confirm-cancel">
              Annuleren
            </Button>
            <Button
              onClick={onCreateInvoice}
              disabled={invoiceBusy}
              className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white font-chivo font-bold uppercase text-xs"
              data-testid="invoice-confirm-create"
            >
              <FileText className="w-4 h-4 mr-1" /> {invoiceBusy ? "Aanmaken…" : "Factuur aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 20C — Verzend-factuur dialog */}
      <Dialog open={invoiceEmailOpen} onOpenChange={setInvoiceEmailOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="offerte-invoice-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-xl">Factuur versturen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-bold">Aan</Label>
              <Input
                type="email"
                value={invoiceEmailForm.to}
                onChange={(e) => setInvoiceEmailForm({ ...invoiceEmailForm, to: e.target.value })}
                className="rounded-none mt-1"
                data-testid="invoice-email-to"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-bold">Onderwerp</Label>
              <Input
                value={invoiceEmailForm.subject}
                onChange={(e) => setInvoiceEmailForm({ ...invoiceEmailForm, subject: e.target.value })}
                className="rounded-none mt-1"
                data-testid="invoice-email-subject"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-bold">Bericht (optioneel — laat leeg voor standaardtekst)</Label>
              <Textarea
                value={invoiceEmailForm.message}
                onChange={(e) => setInvoiceEmailForm({ ...invoiceEmailForm, message: e.target.value })}
                className="rounded-none mt-1 min-h-[120px]"
                data-testid="invoice-email-message"
                placeholder="Standaard begeleidende tekst met betalingskenmerk wordt gebruikt indien leeg."
              />
            </div>
            <p className="text-xs text-gray-500">
              De factuur-PDF wordt automatisch als bijlage meegestuurd.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setInvoiceEmailOpen(false)} data-testid="invoice-email-cancel">
              Annuleren
            </Button>
            <Button
              onClick={onSendInvoiceEmail}
              disabled={invoiceEmailSending || !invoiceEmailForm.to}
              className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white font-chivo font-bold uppercase text-xs"
              data-testid="invoice-email-send"
            >
              <Mail className="w-4 h-4 mr-1" /> {invoiceEmailSending ? "Versturen…" : "Verstuur factuur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
